import { FC, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Box, CircularProgress } from "@mui/material";
import PhCaretLeftSvg from "../../assets/svg-fc/PhCaretLeftSvg";
import PhCaretRightSvg from "../../assets/svg-fc/PhCaretRightSvg";
import PhDownloadSimpleSvg from "../../assets/svg-fc/PhDownloadSimpleSvg";
import PhFillPlaySvg from "../../assets/svg-fc/PhFillPlaySvg";
import PhXSvg from "../../assets/svg-fc/PhXSvg";
import { UI_TOKENS } from "../../constants/designTokens";
import { unzoomed } from "../../context/UiZoomContext";

export interface iChatMediaItem {
	key: string;
	name: string;
	url: string;
	isVideo: boolean;
}

interface iChatMediaViewerProps {
	items: iChatMediaItem[];
	index: number;
	onClose: () => void;
}

const CREAM = "#FBF9EE";

const roundButtonSx = {
	width: 34,
	height: 34,
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	border: "1px solid rgba(251,249,238,0.28)",
	borderRadius: "50%",
	background: "transparent",
	color: CREAM,
	cursor: "pointer",
	transition: "background-color .14s ease",
	"&:hover": { bgcolor: "rgba(251,249,238,0.14)" },
};

const pillButtonSx = {
	...roundButtonSx,
	width: "auto",
	gap: "7px",
	padding: "0 15px",
	borderRadius: UI_TOKENS.radiusPill,
	fontFamily: UI_TOKENS.fontSans,
	fontSize: 12.5,
	fontWeight: 500,
	textDecoration: "none",
};

const navButtonSx = {
	width: 42,
	height: 42,
	flexShrink: 0,
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	borderRadius: "50%",
	border: "none",
	bgcolor: "rgba(255,255,255,0.16)",
	color: "#fff",
	cursor: "pointer",
	"&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
	"&:disabled": { opacity: 0.35, cursor: "default" },
};

const stop = (event: { stopPropagation: () => void }) => event.stopPropagation();

/** Photos and videos shared in a chat, in the same media viewer as the admin portal's caterer gallery. */
const ChatMediaViewer: FC<iChatMediaViewerProps> = ({ items, index, onClose }) => {
	const [currentIndex, setCurrentIndex] = useState(index);
	const item = items[currentIndex];
	const stageUrl = item && !item.isVideo ? item.url : null;
	const [readyUrl, setReadyUrl] = useState<string | null>(null);

	useEffect(() => {
		if (!stageUrl) return undefined;
		let cancelled = false;
		const preload = new Image();
		preload.decoding = "async";
		preload.src = stageUrl;
		const show = () => {
			if (!cancelled) setReadyUrl(stageUrl);
		};
		preload.decode().then(show, show);
		return () => {
			cancelled = true;
		};
	}, [stageUrl]);

	useEffect(() => {
		const handleKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose();
			if (event.key === "ArrowLeft") setCurrentIndex((i) => Math.max(0, i - 1));
			if (event.key === "ArrowRight") setCurrentIndex((i) => Math.min(items.length - 1, i + 1));
		};
		window.addEventListener("keydown", handleKey);
		return () => window.removeEventListener("keydown", handleKey);
	}, [onClose, items.length]);

	if (!item) return null;

	return createPortal(
		<Box
			onClick={onClose}
			role="dialog"
			aria-modal="true"
			aria-label={item.name}
			sx={{
				position: "fixed",
				inset: 0,
				zIndex: 2000,
				bgcolor: "rgba(0,0,0,0.9)",
				display: "flex",
				flexDirection: "column",
				fontFamily: UI_TOKENS.fontSans,
				animation: "ycChatMediaFade 140ms ease",
				"@keyframes ycChatMediaFade": { from: { opacity: 0 }, to: { opacity: 1 } },
			}}
		>
			<Box onClick={stop} sx={{ display: "flex", alignItems: "center", gap: "16px", padding: "16px 20px", flexShrink: 0 }}>
				<Box component="span" sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
					<Box
						component="span"
						sx={{ fontSize: 13.5, fontWeight: 500, color: CREAM, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
					>
						{item.name}
					</Box>
					<Box component="span" sx={{ fontSize: 11.5, color: "rgba(251,249,238,0.58)" }}>
						{currentIndex + 1} of {items.length}
					</Box>
				</Box>
				<Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
					{/* Sendbird file links are cross-origin, so `download` alone is ignored; open in a new tab instead. */}
					<Box component="a" href={item.url} target="_blank" rel="noopener noreferrer" download sx={{ ...pillButtonSx, height: 34 }}>
						<PhDownloadSimpleSvg size={15} />
						Download
					</Box>
					<Box component="button" type="button" onClick={onClose} aria-label="Close" sx={roundButtonSx}>
						<PhXSvg size={15} />
					</Box>
				</Box>
			</Box>

			<Box
				onClick={stop}
				sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: "18px", padding: "0 20px" }}
			>
				{items.length > 1 && (
					<Box
						component="button"
						type="button"
						aria-label="Previous"
						disabled={currentIndex === 0}
						onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
						sx={navButtonSx}
					>
						<PhCaretLeftSvg size={18} />
					</Box>
				)}
				<Box
					sx={{
						flex: 1,
						maxWidth: 980,
						height: "100%",
						maxHeight: unzoomed("68vh"),
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					{item.isVideo ? (
						// Keyed by URL so moving to another item stops this one.
						<Box
							component="video"
							key={item.url}
							src={item.url}
							controls
							autoPlay
							playsInline
							aria-label={item.name}
							sx={{ maxWidth: "100%", maxHeight: "100%", borderRadius: "14px", boxShadow: UI_TOKENS.shadowLg, bgcolor: "#000" }}
						/>
					) : readyUrl === item.url ? (
						<Box
							component="img"
							key={item.url}
							src={item.url}
							alt={item.name}
							decoding="async"
							sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: "14px", boxShadow: UI_TOKENS.shadowLg }}
						/>
					) : (
						<CircularProgress size={34} thickness={3} aria-label={`Loading ${item.name}`} sx={{ color: CREAM }} />
					)}
				</Box>
				{items.length > 1 && (
					<Box
						component="button"
						type="button"
						aria-label="Next"
						disabled={currentIndex === items.length - 1}
						onClick={() => setCurrentIndex((i) => Math.min(items.length - 1, i + 1))}
						sx={navButtonSx}
					>
						<PhCaretRightSvg size={18} />
					</Box>
				)}
			</Box>

			<Box
				onClick={stop}
				sx={{
					flexShrink: 0,
					display: "flex",
					justifyContent: items.length > 1 ? "center" : "flex-start",
					gap: "8px",
					padding: "18px 20px 24px",
					overflowX: "auto",
					scrollbarWidth: "none",
					"&::-webkit-scrollbar": { display: "none" },
					minHeight: 24,
				}}
			>
				{items.length > 1 &&
					items.map((thumb, i) => (
						<Box
							key={thumb.key}
							component="button"
							type="button"
							title={thumb.name}
							onClick={() => setCurrentIndex(i)}
							sx={{
								width: 56,
								height: 56,
								flexShrink: 0,
								padding: 0,
								borderRadius: "8px",
								border: `2px solid ${i === currentIndex ? CREAM : "transparent"}`,
								bgcolor: "#000",
								position: "relative",
								overflow: "hidden",
								opacity: i === currentIndex ? 1 : 0.6,
								cursor: "pointer",
								"&:hover": { opacity: 1 },
							}}
						>
							{thumb.isVideo ? (
								<>
									<Box
										component="video"
										src={`${thumb.url}#t=0.1`}
										muted
										playsInline
										preload="metadata"
										aria-hidden
										sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
									/>
									<Box
										component="span"
										sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", bgcolor: "rgba(0,0,0,0.25)" }}
									>
										<PhFillPlaySvg size={12} />
									</Box>
								</>
							) : (
								<Box
									component="img"
									src={thumb.url}
									alt=""
									loading="lazy"
									decoding="async"
									sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
								/>
							)}
						</Box>
					))}
			</Box>
		</Box>,
		document.body,
	);
};

export default ChatMediaViewer;
